import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Trash2, Star, Lock, Eye, Camera, BadgeCheck, Clock, Home, Plane, MapPin } from "lucide-react";
import api from "../lib/api";
import { PhotoImg, VerifiedBadge, GoldButton, GhostButton, SectionTitle } from "../components/common";
import MapPicker from "../components/MapPicker";
import CityAutocomplete from "../components/CityAutocomplete";
import { ChipMultiSelect } from "../components/ChipMultiSelect";
import { HeightInput } from "../components/HeightInput";
import { AIAssistButton } from "../components/AIAssist";
import { ProfilePreview } from "../components/ProfilePreview";
import CareerFinancialEditor from "../components/CareerFinancialEditor";
import { ProfileStrength } from "../components/ProfileStrength";
import { useAuth } from "../context/AuthContext";

const inputCls = "w-full rounded-xl bg-[#0C0A0A]/60 border border-[#D4AF37]/20 focus:border-[#D4AF37]/60 outline-none px-4 py-2.5 text-sm text-[#FBF8F3] placeholder:text-[#9E978E]";

const toArr = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

export default function MyProfile() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef();
  const [p, setP] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [visiting, setVisiting] = useState({ city: "", state: "", country: "", metro_area: "", market_id: "", latitude: "", longitude: "", arrival_date: "", departure_date: "" });
  const [visitingBusy, setVisitingBusy] = useState(false);
  const [markets, setMarkets] = useState([]);
  const [options, setOptions] = useState({ dating_intentions: [], date_activities: [], interests: [], hobbies: [] });
  const [preview, setPreview] = useState(false);

  useEffect(() => { api.get("/markets").then((r) => setMarkets(r.data.markets || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/profile-options").then((r) => setOptions(r.data || {})).catch(() => {}); }, []);

  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    let data;
    try {
      ({ data } = await api.get("/profile/me"));
    } catch (e) {
      if (e.response?.status === 404) { setLoadError("no_profile"); return; }
      setLoadError("error");
      toast.error("Could not load your profile. Please try again.", { id: "profile-load-error" });
      return;
    }
    setLoadError(null);
    setP(data);
    const vr = data.visiting_raw || {};
    setVisiting({
      city: vr.city || "", state: vr.state || "", country: vr.country || "", metro_area: vr.metro_area || "",
      latitude: vr.latitude ?? "", longitude: vr.longitude ?? "",
      arrival_date: vr.arrival_date || "", departure_date: vr.departure_date || "", market_id: vr.market_id || "",
    });
    setForm({
      display_name: data.display_name || "", gender: data.gender || "", location: data.location || "",
      country: data.country || "", state: data.state || "", city: data.city || "",
      zip_code: data.zip_code || "", metro_area: data.metro_area || "",
      latitude: data.latitude ?? "", longitude: data.longitude ?? "",
      residency: data.residency || "lives_here", show_distance: data.show_distance !== false,
      market_id: data.market_id || "",
      notify_favorite_visiting: !!data.notify_favorite_visiting,
      notify_favorite_visiting_email: !!data.notify_favorite_visiting_email,
      occupation: data.occupation || "", headline: data.headline || "", about_me: data.about_me || "",
      height_cm: data.height_cm || "", preferred_offer: data.preferred_offer || "",
      primary_intention: data.primary_intention || "",
      dating_intentions: data.dating_intentions || [],
      interests: data.interests || [], hobbies: data.hobbies || [],
      date_preferences: data.date_preferences || [],
      languages: (data.languages || []).join(", "),
    });
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile/me", {
        display_name: form.display_name, gender: form.gender || null, location: form.location || null,
        country: form.country || null, state: form.state || null, city: form.city || null,
        zip_code: form.zip_code || null, metro_area: form.metro_area || null,
        latitude: form.latitude !== "" ? Number(form.latitude) : null,
        longitude: form.longitude !== "" ? Number(form.longitude) : null,
        residency: form.residency || null, show_distance: !!form.show_distance,
        market_id: form.market_id || "",
        notify_favorite_visiting: !!form.notify_favorite_visiting,
        notify_favorite_visiting_email: !!form.notify_favorite_visiting_email,
        occupation: form.occupation || null, headline: form.headline || null, about_me: form.about_me || null,
        height_cm: form.height_cm ? Number(form.height_cm) : null,
        preferred_offer: form.preferred_offer ? Number(form.preferred_offer) : null,
        primary_intention: form.primary_intention || null,
        dating_intentions: form.dating_intentions || [],
        interests: form.interests || [], hobbies: form.hobbies || [],
        languages: toArr(form.languages), date_preferences: form.date_preferences || [],
      });
      toast.success("Profile saved");
      refresh(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  const saveVisiting = async () => {
    if (!visiting.city || !visiting.arrival_date || !visiting.departure_date)
      return toast.error("Visiting city, arrival and departure dates are required");
    setVisitingBusy(true);
    try {
      await api.put("/profile/visiting", {
        city: visiting.city, state: visiting.state || null, country: visiting.country || null,
        metro_area: visiting.metro_area || null,
        market_id: visiting.market_id || null,
        latitude: visiting.latitude !== "" ? Number(visiting.latitude) : null,
        longitude: visiting.longitude !== "" ? Number(visiting.longitude) : null,
        arrival_date: visiting.arrival_date, departure_date: visiting.departure_date,
      });
      toast.success("Visiting plan saved"); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = Array.isArray(d) ? (d[0]?.msg || "Invalid visiting details") : (d || "Could not save visiting plan");
      toast.error(msg);
    } finally { setVisitingBusy(false); }
  };

  const removeVisiting = async () => {
    try {
      await api.delete("/profile/visiting");
      setVisiting({ city: "", state: "", country: "", metro_area: "", latitude: "", longitude: "", arrival_date: "", departure_date: "" });
      toast.success("Visiting plan removed"); load();
    } catch (e) { toast.error("Could not remove"); }
  };

  const upload = async (file, isPrivate = false) => {
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Please upload a JPG, PNG or WEBP image.");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("That image is too large. Maximum size is 10MB.");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/photos?is_private=${isPrivate}`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      toast.success("Photo uploaded"); load();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      toast.error(detail || (status ? `Upload failed (${status}). Your other details are safe — please try again.` : "Upload failed — please check your connection and retry."));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const delPhoto = async (id) => { await api.delete(`/photos/${id}`); load(); };
  const makeMain = async (id) => { const ids = p.photos.map((x) => x.photo_id); await api.post("/photos/reorder", { order: [id, ...ids.filter((x) => x !== id)] }); load(); };
  const togglePrivacy = async (ph) => { await api.post(`/photos/${ph.photo_id}/privacy`, { is_private: !ph.is_private }); load(); };

  if (!p && loadError === "no_profile") return (
    <div data-testid="my-profile-no-profile" className="min-h-[50vh] flex flex-col items-center justify-center text-center px-6 gap-2">
      <p className="text-lg text-[#FBF8F3]">This account doesn't have a member profile.</p>
      <p className="text-sm text-[#9E978E]">Member profiles are created when someone joins as a member. Everything else in the app works as usual.</p>
    </div>
  );
  if (!p && loadError === "error") return (
    <div data-testid="my-profile-load-error" className="min-h-[50vh] flex flex-col items-center justify-center text-center px-6 gap-3">
      <p className="text-[#FBF8F3]">We couldn't load your profile.</p>
      <GhostButton data-testid="my-profile-retry-btn" onClick={() => { setLoadError(null); load(); }}>Try again</GhostButton>
    </div>
  );
  if (!p) return <div data-testid="my-profile-loading" className="min-h-[50vh] flex items-center justify-center text-[#9E978E]">Loading…</div>;

  const approval = p.approval_status;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <SectionTitle eyebrow="Your Presence" title="My Profile" />
        <div className="flex items-center gap-3">
          <VerifiedBadge selfie={p.selfie_verified} id={p.id_verified} size="md" />
          {approval === "approved" && <span className="inline-flex items-center gap-1.5 text-xs text-[#6FD79E] bg-[#2E7D52]/15 border border-[#2E7D52]/40 rounded-full px-3 py-1"><BadgeCheck size={13} /> Approved</span>}
          {approval === "pending" && <span className="inline-flex items-center gap-1.5 text-xs text-[#E0B84A] bg-[#C89528]/15 border border-[#C89528]/40 rounded-full px-3 py-1"><Clock size={13} /> Pending Approval</span>}
          {approval === "rejected" && <span className="text-xs text-[#E58CA0] bg-[#8B263E]/20 border border-[#8B263E]/40 rounded-full px-3 py-1">Needs Changes</span>}
        </div>
      </div>

      {approval !== "approved" && (
        <div className="rounded-xl bg-[#541224]/20 border border-[#D4AF37]/20 px-4 py-3 mb-6 text-sm text-[#D8D2C9]">
          Complete your profile with a photo and details. Once an admin approves it, you can make and receive date offers.
          <button onClick={() => navigate("/verify")} className="text-[#D4AF37] ml-2 underline underline-offset-2">Get verified</button>
        </div>
      )}

      <ProfileStrength form={form} photos={p.photos || []} mainPhotoId={p.main_photo_id}
        selfieVerified={p.selfie_verified} idVerified={p.id_verified} onGetVerified={() => navigate("/verify")} />

      <div className="rounded-2xl border border-white/10 bg-[#141113]/60 p-5 mb-6">
        <h3 className="font-serif text-xl text-[#FBF8F3] mb-4 flex items-center gap-2"><Camera size={18} className="text-[#D4AF37]" /> Photos</h3>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {(p.photos || []).map((ph) => (
            <div key={ph.photo_id} className="relative group aspect-[3/4] rounded-xl overflow-hidden border border-white/10">
              <PhotoImg photoId={ph.photo_id} className="w-full h-full object-cover" />
              {p.main_photo_id === ph.photo_id && <span className="absolute top-1.5 left-1.5 bg-[#D4AF37] text-[#0C0A0A] text-[9px] font-bold rounded-full px-2 py-0.5">MAIN</span>}
              {ph.is_private && <span className="absolute top-1.5 right-1.5 bg-black/60 text-white rounded-full p-1"><Lock size={11} /></span>}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                <button onClick={() => makeMain(ph.photo_id)} title="Set main" className="h-8 w-8 rounded-full bg-[#541224] text-[#D4AF37] flex items-center justify-center"><Star size={14} /></button>
                <button onClick={() => togglePrivacy(ph)} title="Toggle privacy" className="h-8 w-8 rounded-full bg-[#541224] text-[#D4AF37] flex items-center justify-center">{ph.is_private ? <Eye size={14} /> : <Lock size={14} />}</button>
                <button data-testid={`delete-photo-${ph.photo_id}`} onClick={() => delPhoto(ph.photo_id)} title="Delete" className="h-8 w-8 rounded-full bg-[#8B263E] text-white flex items-center justify-center"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          <button data-testid="add-photo-btn" onClick={() => fileRef.current.click()} disabled={uploading} className="aspect-[3/4] rounded-xl border-2 border-dashed border-[#D4AF37]/30 flex flex-col items-center justify-center text-[#D4AF37] hover:border-[#D4AF37]/60 gap-1">
            <Upload size={20} /><span className="text-xs">{uploading ? "…" : "Add"}</span>
          </button>
        </div>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0], false)} />
        <p data-testid="private-photos-help" className="text-xs text-[#9E978E] mt-3">Hover a photo to set as main, toggle private, or delete. <span className="text-[#C9A96E]">Private Photos — only bidders whose offers you accept can view these photos.</span> Private photos are never shown publicly and are optional.</p>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#141113]/60 p-5 space-y-4">
        <div className="rounded-xl border border-[#D4AF37]/15 bg-[#0C0A0A]/40 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-serif text-lg text-[#FBF8F3]">Location</h4>
            <span className="text-[10px] text-[#9E978E]">Public: DateBid Market only</span>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">DateBid Location Market <span className="text-[#D4AF37]">(public · required for discovery)</span></label>
            <select data-testid="profile-market-select" value={form.market_id || ""} onChange={(e) => setForm({ ...form, market_id: e.target.value })} className={inputCls}>
              <option value="">— Select your market —</option>
              {markets.map((m) => <option key={m.market_id} value={m.market_id}>{m.display_name}{m.region ? ` · ${m.region}` : ""} ({m.type})</option>)}
            </select>
            <p className="text-[10px] text-[#9E978E] mt-1">Your market is your public location. The fields below are private and optional (used only for distance and future features — never shown publicly).</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Search your city <span className="text-[#751A32]">(optional · private)</span></label>
            <CityAutocomplete testid="home-city-autocomplete" value={form.city ? [form.city, form.state, form.country].filter(Boolean).join(", ") : ""}
              onSelect={(r) => setForm((f) => ({ ...f, city: r.city || f.city, state: r.state || f.state,
                country: r.country || f.country, metro_area: r.metro_area || f.metro_area,
                latitude: r.latitude ?? f.latitude, longitude: r.longitude ?? f.longitude }))} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Country</label><input data-testid="loc-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className={inputCls} /></div>
            <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">State</label><input data-testid="loc-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={inputCls} /></div>
            <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">City</label><input data-testid="loc-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} /></div>
            <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">ZIP Code <span className="text-[#751A32]">(private)</span></label><input data-testid="loc-zip" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} className={inputCls} /></div>
            <div className="col-span-2"><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Metro Area</label><input data-testid="loc-metro" value={form.metro_area} onChange={(e) => setForm({ ...form, metro_area: e.target.value })} className={inputCls} placeholder="e.g. New York Metro" /></div>
            <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Latitude <span className="text-[#751A32]">(private)</span></label><input data-testid="loc-lat" type="number" step="any" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} className={inputCls} /></div>
            <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Longitude <span className="text-[#751A32]">(private)</span></label><input data-testid="loc-lng" type="number" step="any" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} className={inputCls} /></div>
            <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Residency</label><select data-testid="loc-residency" value={form.residency} onChange={(e) => setForm({ ...form, residency: e.target.value })} className={inputCls}><option value="lives_here">Lives Here</option><option value="visiting">Visiting</option></select></div>
          </div>
          <MapPicker lat={form.latitude !== "" ? Number(form.latitude) : null} lng={form.longitude !== "" ? Number(form.longitude) : null}
            testidPrefix="home-map"
            onLocation={(loc) => setForm((f) => ({ ...f,
              latitude: loc.latitude ?? f.latitude, longitude: loc.longitude ?? f.longitude,
              city: loc.city || f.city, state: loc.state || f.state, country: loc.country || f.country,
              zip_code: loc.zip || f.zip_code, metro_area: loc.metro || f.metro_area }))} />
          <label className="flex items-center justify-between text-sm text-[#D8D2C9] pt-1">
            <span>Show my approximate distance to others</span>
            <input data-testid="loc-show-distance" type="checkbox" checked={!!form.show_distance} onChange={(e) => setForm({ ...form, show_distance: e.target.checked })} className="accent-[#D4AF37] h-4 w-4" />
          </label>
          <p className="text-[11px] text-[#9E978E] flex items-start gap-1.5"><Lock size={12} className="text-[#751A32] mt-0.5 shrink-0" /> Never shown publicly: exact ZIP, exact GPS coordinates, and exact address. Others only see your City + State and an optional approximate distance.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Display Name</label><input data-testid="profile-name-input" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className={inputCls} /></div>
          <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Gender</label><select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className={inputCls}><option value="">Select</option><option>Female</option><option>Male</option><option>Non-binary</option></select></div>
          <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Occupation</label><input value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })} className={inputCls} /></div>
          <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Preferred Offer ($)</label><input data-testid="profile-offer-input" type="number" value={form.preferred_offer} onChange={(e) => setForm({ ...form, preferred_offer: e.target.value })} className={inputCls} /></div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Height</label>
          <HeightInput testid="profile-height" valueCm={form.height_cm ? Number(form.height_cm) : ""} onChange={(cm) => setForm({ ...form, height_cm: cm || "" })} />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Headline</label>
            <AIAssistButton testid="ai-headline" endpoint="/profile/ai/headline" draft={form.headline} label="Create with AI" onAccept={(t) => setForm((f) => ({ ...f, headline: t }))} />
          </div>
          <input data-testid="profile-headline-input" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className={inputCls} placeholder="A short line that captures you" />
          <p className="text-[11px] text-[#9E978E] mt-1">Create a short introduction that makes someone want to learn more about you.</p>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">About Me</label>
            <AIAssistButton testid="ai-about" endpoint="/profile/ai/about" draft={form.about_me} multiline label="Improve with AI" onAccept={(t) => setForm((f) => ({ ...f, about_me: t }))} />
          </div>
          <textarea data-testid="profile-about-input" rows={4} value={form.about_me} onChange={(e) => setForm({ ...form, about_me: e.target.value })} className={inputCls + " resize-none"} placeholder="Tell people about your personality, lifestyle, interests, and what makes you unique." />
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-wide text-[#C9A96E] font-semibold">Primary Dating Intention <span className="text-[#D4AF37]">(required)</span></label>
          <p className="text-[11px] text-[#9E978E] mb-2">What are you mainly hoping to find on DateBid?</p>
          <ChipMultiSelect testid="primary-intention" singleSelect options={options.dating_intentions || []}
            value={form.primary_intention ? [form.primary_intention] : []}
            onChange={(v) => setForm({ ...form, primary_intention: v[0] || "" })} />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wide text-[#C9A96E] font-semibold">Also Interested In <span className="text-[#9E978E]">(optional)</span></label>
          <ChipMultiSelect testid="dating-intentions" hint="You can select more than one." options={options.dating_intentions || []}
            value={form.dating_intentions || []} onChange={(v) => setForm({ ...form, dating_intentions: v })} />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wide text-[#C9A96E] font-semibold">Ideal Date Activities</label>
          <ChipMultiSelect testid="date-activities" hint="Choose the activities you'd genuinely enjoy. The actual plan is decided with your date later." options={options.date_activities || []}
            value={form.date_preferences || []} onChange={(v) => setForm({ ...form, date_preferences: v })} />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wide text-[#C9A96E] font-semibold">Interests</label>
          <ChipMultiSelect testid="interests" hint="Select the things you genuinely enjoy. You can choose multiple." options={options.interests || []}
            value={form.interests || []} onChange={(v) => setForm({ ...form, interests: v })} />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wide text-[#C9A96E] font-semibold">Hobbies</label>
          <ChipMultiSelect testid="hobbies" hint="What do you enjoy doing in your free time?" options={options.hobbies || []}
            value={form.hobbies || []} onChange={(v) => setForm({ ...form, hobbies: v })} />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Languages (comma separated)</label>
          <input value={form.languages} onChange={(e) => setForm({ ...form, languages: e.target.value })} className={inputCls} placeholder="English, French" />
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <GhostButton data-testid="profile-preview-btn" onClick={() => setPreview(true)} className="sm:flex-1">Preview My Profile</GhostButton>
          <GoldButton data-testid="profile-save-btn" onClick={save} disabled={saving} className="sm:flex-1">{saving ? "Saving…" : "Save Profile"}</GoldButton>
        </div>
      </div>

      {preview && (
        <ProfilePreview form={form} photos={p.photos || []} mainPhotoId={p.main_photo_id}
          marketLabel={markets.find((m) => m.market_id === form.market_id)?.display_name}
          age={p.age} selfieVerified={p.selfie_verified} idVerified={p.id_verified}
          onClose={() => setPreview(false)} />
      )}

      <CareerFinancialEditor initial={p.career_financial} onSaved={load} />

      <div className="rounded-2xl border border-white/10 bg-[#141113]/60 p-5 mt-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-xl text-[#FBF8F3] flex items-center gap-2"><Plane size={18} className="text-[#D4AF37]" /> Visiting Plans</h3>
          {p.visiting && <span className="text-[11px] text-[#C9A96E]">{p.visiting.active ? "Active now" : "Upcoming"}</span>}
        </div>
        <p className="text-xs text-[#9E978E]">Traveling soon? Add a visiting city so members there can find you during your trip. It becomes inactive automatically after your departure date. Your home city is always preserved.</p>
        <div>
          <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Search visiting city</label>
          <CityAutocomplete testid="visit-city-autocomplete" value={visiting.city ? [visiting.city, visiting.state, visiting.country].filter(Boolean).join(", ") : ""}
            onSelect={(r) => setVisiting((v) => ({ ...v, city: r.city || v.city, state: r.state || v.state,
              country: r.country || v.country, metro_area: r.metro_area || v.metro_area,
              latitude: r.latitude ?? v.latitude, longitude: r.longitude ?? v.longitude }))} />
        </div>
        <div className="mb-3">
          <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Destination Market <span className="text-[#D4AF37]">(so others can find you visiting)</span></label>
          <select data-testid="visit-market-select" value={visiting.market_id || ""} onChange={(e) => setVisiting({ ...visiting, market_id: e.target.value })} className={inputCls}>
            <option value="">— Optional: select destination market —</option>
            {markets.map((m) => <option key={m.market_id} value={m.market_id}>{m.display_name}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Visiting City</label><input data-testid="visit-city" value={visiting.city} onChange={(e) => setVisiting({ ...visiting, city: e.target.value })} className={inputCls} /></div>
          <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">State</label><input data-testid="visit-state" value={visiting.state} onChange={(e) => setVisiting({ ...visiting, state: e.target.value })} className={inputCls} /></div>
          <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Arrival</label><input data-testid="visit-arrival" type="date" value={visiting.arrival_date} onChange={(e) => setVisiting({ ...visiting, arrival_date: e.target.value })} className={inputCls} /></div>
          <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Departure</label><input data-testid="visit-departure" type="date" value={visiting.departure_date} onChange={(e) => setVisiting({ ...visiting, departure_date: e.target.value })} className={inputCls} /></div>
        </div>
        <MapPicker lat={visiting.latitude !== "" ? Number(visiting.latitude) : null} lng={visiting.longitude !== "" ? Number(visiting.longitude) : null}
          testidPrefix="visit-map"
          onLocation={(loc) => setVisiting((v) => ({ ...v,
            latitude: loc.latitude ?? v.latitude, longitude: loc.longitude ?? v.longitude,
            city: loc.city || v.city, state: loc.state || v.state, country: loc.country || v.country, metro_area: loc.metro || v.metro_area }))} />
        <div className="flex gap-3">
          <GoldButton data-testid="visit-save-btn" onClick={saveVisiting} disabled={visitingBusy} className="flex-1">{visitingBusy ? "Saving…" : "Save Visiting Plan"}</GoldButton>
          {p.visiting && <GhostButton data-testid="visit-remove-btn" onClick={removeVisiting} className="flex-1">Remove Visiting</GhostButton>}
        </div>
        <div className="pt-2 border-t border-white/8 mt-2 space-y-3">
          <h4 className="font-serif text-base text-[#FBF8F3]">Visit alerts</h4>
          <p className="text-[11px] text-[#9E978E]">Get notified when one of your favorites has a trip to your home city/area (active now or starting within 7 days). Off by default — you're in control. We only use public city/state and trip dates, never exact GPS or address.</p>
          <label className="flex items-center justify-between text-sm text-[#D8D2C9]">
            <span>In-app visit alerts</span>
            <input data-testid="notify-visit-inapp" type="checkbox" checked={!!form.notify_favorite_visiting}
              onChange={(e) => setForm({ ...form, notify_favorite_visiting: e.target.checked })} className="accent-[#D4AF37] h-4 w-4" />
          </label>
          <label className="flex items-center justify-between text-sm text-[#D8D2C9]">
            <span>Email visit alerts</span>
            <input data-testid="notify-visit-email" type="checkbox" checked={!!form.notify_favorite_visiting_email}
              onChange={(e) => setForm({ ...form, notify_favorite_visiting_email: e.target.checked })} className="accent-[#D4AF37] h-4 w-4" />
          </label>
          <p className="text-[11px] text-[#9E978E]">Changes are saved with the <span className="text-[#C9A96E]">Save Profile</span> button above.</p>
        </div>
      </div>
    </div>
  );
}
